import React from "react";

import _ from 'lodash';

import {
  PAGE, BASIC_TEXT, TEXT, IMAGE, VIDEO, BUTTON, SHAPE, LINE, RECTANGLE,
  IFRAME, HTML, ANCHOR,
  FORM, FORM_STEP, FORM_FIELD, FORM_SUBMIT,
  SECTION, SECTION_COLUMN,
  LIVE_ELEMENT, LIVE_ELEMENT_CONTAINER,
  CONTENT, CONTENT_CARD, CONTENT_CARD_TEMPLATE, CONTENT_PAGINATION_BUTTON, ASSET,
  GROUP, MODAL, EXTERNAL_FORM, MENU_CONTAINER, MENU, HOTSPOT, CONTENT_FILTER,
  CONTENT_SEARCH, CONTENT_FILTER_CONTAINER, CONTENT_EMPTY_MESSAGE, CONTENT_LOADING_MESSAGE, CAROUSEL, CAROUSEL_CARD, SEEK
} from '../../../constants/widgetTypes';

import { DESKTOP } from "../../../constants/breakpoints";

import { BasicTextWidget } from "../basicText";
import { ButtonWidget } from "../button";
import { ImageWidget } from "../image";
import { VideoWidget } from "../video";
import { PageWidget } from "../page";
import { TextWidget } from "../text";
import { ShapeWidget } from "../shape";
import { RectangleWidget } from "../rectangle";
import { LineWidget } from "../line";
import { IFrameWidget } from "../iframe";
import { HtmlWidget } from "../html";
import { FormWidget } from '../form';
import { FormStepWidget } from '../form/steps';
import { FormFieldWidget } from '../form/fields';
import { SubmitButtonWidget } from '../form/submit';
import { AnchorWidget } from "../anchor";
import { LiveElementWidget } from "../liveElements";
import { LiveElementContainerWidget } from "../liveElements/container";
import { SectionWidget } from '../section';
import { SectionColumnWidget } from '../section/column';
import { ContentWidget } from "../content";
import { ContentCardWidget } from "../content/card";
import { PlaceholderCardWidget } from "../content/placeholder";
import { FilterWidget } from "../content/filters";
import { FilterContainerWidget } from "../content/filters/filterContainer";
import { PaginationButtonWidget } from "../content/pagination";
import { EmptyMessageWidget } from "../content/emptyMessage";
import { LoadingWidget } from "../content/loading";
import { GroupWidget } from '../group';
import { ModalWidget } from "../modal";
import { SearchContainerWidget } from "../content/filters/searchContainer";
import { MenuWidget } from "../menu";
import { MenuContainerWidget } from "../menu/menuContainer";
import { ExternalFormWidget } from "../externalForm";
import { HotspotWidget } from '../hotspot';
import {SeekWidget } from "../seek";
import { CarouselWidget } from "../carousel";
import { CarouselCardWidget } from "../carousel/card";
import { AssetWidget } from './../asset';

import { getStylesForLayout } from "../widget.helper";
import { useWidgetChildrenSelector, useContainerSelector } from "./hooks";

import styles from '../widget.module.scss';

export const WidgetsContext = React.createContext({
  selector: null,
  breakpoint: DESKTOP,
  shouldZoom: true,
  domain: null,
  development: false,
  createLiveElementContext: (id) => { },
  createGatingContext: (id) => { },
  fetchGating: (id) => { }
});

export const FormContext = React.createContext({
  useForm: () => { },
  useFormValue: ['', () => { }],
  submitForm: () => { },
  formSuccess: false
})

export const SeekContext = React.createContext({
  debug: false,
  seekSession: null,
  seekAnswerForQuestion: (widget, id, question, pageSession, pageId, storyboard) => { },
  updateSeekHelpful: (widget, chatId, helpful) => { },
  invalidateSeekSession: (widget) => { }
});

export const GatingContext = React.createContext({
  triggerGating: () => { },
  type: null,
  payload: null,
  gating: null,
  configuration: null,
  configurations: [],
  assetConfigurations: []
});

export const ModalContext = React.createContext({
  close: () => { }
})

export const EmbedContext = React.createContext({
  embedded: false
})

const WidgetComponent = ({ widgetProps, breakpoint, ...rest }) => {
  const id = _.get(widgetProps, 'uid');
  const container = useContainerSelector(id);
  const widgetChildren = useWidgetChildrenSelector(id);

  const type = _.get(widgetProps, 'type');
  const Component = getWidgetComponentFotType(type);

  if (!Component) {
    console.warn(`Component not found for type ${type}`);
    return false;
  }

  const layout = _.get(widgetProps, `properties.${breakpoint}.layout`);
  var widgetStyle = getStylesForLayout(layout, breakpoint, container);

  var children = widgetChildren.map(item => {
    return <Widget key={item.uid} widgetProps={item} breakpoint={breakpoint} />
  });

  return React.createElement(Component, {
    widgetProps: widgetProps,
    className: styles.widget,
    key: id,
    breakpoint: breakpoint,
    style: widgetStyle,
    ...rest
  }, children);
}

const getWidgetComponentFotType = (type) => {

  switch (type) {
    case PAGE:
      return PageWidget;
    case BASIC_TEXT:
      return BasicTextWidget;
    case TEXT:
      return TextWidget;
    case IMAGE:
      return ImageWidget;
    case VIDEO:
      return VideoWidget;
    case BUTTON:
      return ButtonWidget;
    case SHAPE:
      return ShapeWidget;
    case LINE:
      return LineWidget;
    case RECTANGLE:
      return RectangleWidget;
    case IFRAME:
      return IFrameWidget;
    case HTML:
      return HtmlWidget;
    case EXTERNAL_FORM:
      return ExternalFormWidget;
    case FORM:
      return FormWidget;
    case FORM_STEP:
      return FormStepWidget;
    case FORM_FIELD:
      return FormFieldWidget;
    case FORM_SUBMIT:
      return SubmitButtonWidget;
    case ANCHOR:
      return AnchorWidget;
    case LIVE_ELEMENT:
      return LiveElementWidget;
    case LIVE_ELEMENT_CONTAINER:
      return LiveElementContainerWidget;
    case SECTION:
      return SectionWidget;
    case SECTION_COLUMN:
      return SectionColumnWidget;
    case CONTENT:
      return ContentWidget;
    case CONTENT_CARD:
      return ContentCardWidget;
    case CONTENT_CARD_TEMPLATE:
      return PlaceholderCardWidget;
    case CONTENT_FILTER:
      return FilterWidget;
    case CONTENT_FILTER_CONTAINER:
      return FilterContainerWidget;
    case CONTENT_SEARCH:
      return SearchContainerWidget;
    case CONTENT_PAGINATION_BUTTON:
      return PaginationButtonWidget;
    case CONTENT_EMPTY_MESSAGE:
      return EmptyMessageWidget;
    case CONTENT_LOADING_MESSAGE:
      return LoadingWidget;
    case GROUP:
      return GroupWidget;
    case MODAL:
      return ModalWidget;
    case MENU:
      return MenuWidget;
    case MENU_CONTAINER:
      return MenuContainerWidget;
    case CAROUSEL:
      return CarouselWidget;
    case CAROUSEL_CARD:
      return CarouselCardWidget;
    case HOTSPOT:
      return HotspotWidget;
    case ASSET:
      return AssetWidget;
    case SEEK:
      return SeekWidget;
    default:
      return false
  }
}

const widgetEquals = function (prevProps, nextProps) {
  const paths = ['widgetProps', 'breakpoint'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const Widget = React.memo(WidgetComponent, widgetEquals);
